// Composer Studio — 작성 탭 위젯 선택 계산(순수 함수).
//
// 양식 탭 노드 축의 containerSiblingIds(EditorPage)와 **같은 규약**의 위젯 축 대응물이다:
// Ctrl+A는 "화면 전체"가 아니라 **선택 위치의 직속 컨테이너 형제 전체**를 잡는다(scope-aware, Figma식).
// 컨테이너 안에서 눌렀으면 그 안 형제만, 최상위면 root 전체 — 두 탭의 Ctrl+A 결이 어긋나지 않게 한다.

import type { ComposerWidget } from '@safetyhub/shared';
import { isChildrenContainer } from '@safetyhub/report-composer';

/**
 * 컨테이너 id의 직속 자식 목록을 찾는다(못 찾으면 null).
 *
 * 자식 축은 rc `isChildrenContainer`를 **그대로 import**해 쓴다(kind 목록 복제 금지 — 새 컨테이너
 * kind가 생겼을 때 여기만 누락되면 작성 탭 Ctrl+A가 조용히 죽는다).
 * branch는 자식이 **슬롯**이라 위젯 형제 개념이 없다 — 대신 **슬롯 id**로 물으면 그 슬롯의 children을
 * 돌려준다(`widgetParentId`가 슬롯 안 위젯의 부모로 슬롯 id를 반환하는 규약과 짝).
 * branch **위젯 id** 자체로 물으면 null(슬롯을 고르지 않고는 형제가 정해지지 않는다).
 */
function childrenOfContainerId(
  tree: ComposerWidget[],
  containerId: string,
): ComposerWidget[] | null {
  let found: ComposerWidget[] | null = null;
  const walk = (list: ComposerWidget[]): void => {
    for (const w of list) {
      if (found) return;
      if (isChildrenContainer(w)) {
        if (w.id === containerId) {
          found = w.children;
          return;
        }
        walk(w.children);
      } else if (w.kind === 'branch') {
        for (const slot of w.branches) {
          if (found) return;
          if (slot.id === containerId) {
            found = slot.children;
            return;
          }
          walk(slot.children);
        }
      }
    }
  };
  walk(tree);
  return found;
}

/**
 * 작성 탭 Ctrl+A 대상 — 선택 위치의 **직속 컨테이너 형제** id 목록(삽입 순서 보존).
 *
 * 우선순위:
 *   1) 선택 위젯이 있으면 → 그 위젯의 부모 컨테이너 자식 전체(부모가 root면 root 목록).
 *   2) 선택이 없으면 → isolation 컨텍스트(모달/섹션 드릴인)의 자식 전체.
 *   3) 둘 다 없으면 → root 목록.
 *
 * `parentIdOf`는 rc `widgetParentId`를 그대로 주입받는다(부모 판정 규약을 복제하지 않기 위해 —
 * 특히 branch 슬롯 처리가 갈라지면 안 된다). 반환값 의미도 그 규약을 따른다:
 *   `null`=root 직속 / `string`=그 컨테이너(또는 슬롯) 아래 / `undefined`=트리에서 못 찾음.
 *
 * **`undefined`(트리 미발견)면 빈 배열**을 돌려준다 — formTable 셀 위젯이 대표 사례이고, 그 경우
 * 표 셀 Ctrl+A(작성 탭 셀 포커스 핸들러)가 이미 자기 몫을 하므로 여기서 개입하면 안 된다.
 */
export function composerWidgetSiblingIds(
  tree: ComposerWidget[],
  selectedWidgetId: string | null,
  writeSelectionContextId: string | null,
  parentIdOf: (tree: ComposerWidget[], id: string) => string | null | undefined,
): string[] {
  const rootIds = () => tree.map((w) => w.id);

  if (selectedWidgetId) {
    const parentId = parentIdOf(tree, selectedWidgetId);
    if (parentId === undefined) return []; // 트리 밖(표 셀 위젯 등) — 개입하지 않는다
    if (parentId === null) return rootIds();
    const children = childrenOfContainerId(tree, parentId);
    return children ? children.map((w) => w.id) : [];
  }

  if (writeSelectionContextId) {
    const children = childrenOfContainerId(tree, writeSelectionContextId);
    // 컨텍스트가 트리에서 사라진 경우(삭제/undo 잔존 id)는 root로 폴백 — 빈 선택보다 낫다.
    if (children) return children.map((w) => w.id);
  }

  return rootIds();
}
